import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Flag, IndianRupee, Replace, Camera, Sparkles, Clock,
} from "lucide-react";
import { getActiveProjectGraph } from "../../data/sampleGraph";
import { DESIGNER_RULES } from "../../data/designerRules";
import type { GraphNode } from "../../types/ontology";
import { predicateToText } from "../../lib/predicateRender";
import { cls } from "../../lib/cls";
import { formatMoney } from "../../lib/i18n/format";
import { ageDays, daysUntil } from "../../lib/temporal";

/* ProjectActivityFeed — designer + client view of the last things the
 * agent did on *this* project. Every row is derived from a real
 * Alert / Approval / Milestone node in the project graph, never from
 * canned copy. If a row has no graph anchor, it doesn't render.
 *
 * The Agent tab used to host a generic "what the agent does" pitch.
 * That sits in the diagram + "how it thinks" panel above. This
 * panel answers the question a designer actually asks when they
 * open the tab:  "what did it do for *me*, on *this* project?". */

interface ActivityRow {
  id: string;
  when: string;
  icon: JSX.Element;
  tone: "warn" | "info" | "ok" | "neutral";
  title: string;
  body: string;
  cited: GraphNode[];
  ruleId?: string;
  ruleTitle?: string;
  rulePredicate?: string;
  cta?: { label: string; tone: "primary" | "ghost" };
}

const TONE: Record<ActivityRow["tone"], string> = {
  warn:    "bg-foyr/10 text-foyr-deep border-foyr/30",
  info:    "bg-sky-50 text-sky-800 border-sky-200",
  ok:      "bg-fern/10 text-fern border-fern/30",
  neutral: "bg-cream-dark text-ink-700 border-border",
};

function findNode(id: string): GraphNode | undefined {
  return getActiveProjectGraph().nodes.find((n) => n.id === id);
}
function neighbours(id: string, edgeKind?: string): GraphNode[] {
  return getActiveProjectGraph().edges
    .filter((e) => e.source === id && (!edgeKind || e.kind === edgeKind))
    .map((e) => findNode(e.target))
    .filter((n): n is GraphNode => !!n);
}
function findRule(id: string) {
  return DESIGNER_RULES.find((r) => r.id === id);
}

function valueLabel(v: unknown): string {
  if (typeof v === "number" && v >= 1000) return v.toLocaleString("en-IN");
  if (v === null || v === undefined) return "—";
  return String(v);
}

function deriveActivity(): ActivityRow[] {
  const rows: ActivityRow[] = [];

  // 1) Alerts — the agent's most recent "thing it caught".
  for (const alert of getActiveProjectGraph().nodes.filter((n) => n.kind === "Alert")) {
    const cited = neighbours(alert.id, "OF").concat(neighbours(alert.id, "RULE_FIRED_FROM"));
    const ruleNode = cited.find((n) => n.kind === "Rule");
    const rule = ruleNode ? findRule(ruleNode.id) : undefined;
    const severity = (alert.attrs["severity"] ?? "info") as string;
    const tone: ActivityRow["tone"] =
      severity === "warn" || severity === "high" ? "warn" :
      severity === "info" ? "info" : "neutral";
    rows.push({
      id: `alert-${alert.id}`,
      when: alert.attrs["ageMinutes"] ? `${alert.attrs["ageMinutes"]}m ago` : "just now",
      icon: <Flag className="w-3.5 h-3.5" />,
      tone,
      title: `Alert · ${alert.label}`,
      body: rule
        ? `Fired by rule “${rule.title}”.`
        : `Severity ${severity}.`,
      cited: cited.filter((n) => n.kind !== "Rule"),
      ...(rule
        ? { ruleId: rule.id, ruleTitle: rule.title, rulePredicate: predicateToText(rule.predicate) }
        : {}),
      cta: { label: "Open in cockpit", tone: "ghost" },
    });
  }

  // 2) Approvals — agent watches state + age.
  for (const ap of getActiveProjectGraph().nodes.filter((n) => n.kind === "Approval")) {
    const state = (ap.attrs["state"] ?? "pending") as string;
    const age = ageDays(ap);
    const tone: ActivityRow["tone"] =
      state === "approved" ? "ok" :
      state === "pending" && age >= 3 ? "warn" : "info";
    rows.push({
      id: `approval-${ap.id}`,
      when: state === "approved" ? `${age}d ago` : age > 0 ? `${age}d pending` : "today",
      icon: <ShieldCheck className="w-3.5 h-3.5" />,
      tone,
      title: `${state === "approved" ? "Approval logged" : state === "pending" ? "Approval waiting" : "Approval drafted"} · ${ap.label}`,
      body: state === "approved"
        ? `Approved by ${valueLabel(ap.attrs["role"])}.`
        : state === "pending" && age >= 3
          ? `${age} days with the ${valueLabel(ap.attrs["role"])} — the agent nudged a follow-up.`
          : `Held in ${state}.`,
      cited: [ap],
    });
  }

  // 3) Milestones approaching — agent surfaces slack risk.
  for (const ms of getActiveProjectGraph().nodes.filter((n) => n.kind === "Milestone")) {
    const dueIn = daysUntil(ms);
    const slack = Number(ms.attrs["slackDays"] ?? 0);
    if (dueIn > 60 || dueIn < 0) continue;
    const tone: ActivityRow["tone"] = slack <= 0 ? "warn" : slack <= 3 ? "info" : "ok";
    rows.push({
      id: `ms-${ms.id}`,
      when: `due in ${dueIn}d`,
      icon: <Clock className="w-3.5 h-3.5" />,
      tone,
      title: `Milestone watched · ${ms.label}`,
      body: slack <= 0
        ? `Slack exhausted. The agent flagged knock-on risk to handover.`
        : `${slack}d slack left.`,
      cited: [ms],
    });
  }

  // 4) Budget pressure — sum applied BudgetLines vs Project cap.
  const project = getActiveProjectGraph().nodes.find((n) => n.kind === "Project");
  if (project && typeof project.attrs["budgetCap"] === "number") {
    const cap = project.attrs["budgetCap"];
    const lines = getActiveProjectGraph().nodes.filter((n) => n.kind === "BudgetLine");
    const spent = lines.reduce((acc, l) => acc + (typeof l.attrs["amount"] === "number" ? (l.attrs["amount"] as number) : 0), 0);
    const ratio = cap > 0 ? spent / cap : 0;
    const tone: ActivityRow["tone"] = ratio >= 0.9 ? "warn" : ratio >= 0.7 ? "info" : "ok";
    rows.push({
      id: `budget-${project.id}`,
      when: "live",
      icon: <IndianRupee className="w-3.5 h-3.5" />,
      tone,
      title: `Budget · ${Math.round(ratio * 100)}% committed`,
      body: `${formatMoney(spent)} of ${formatMoney(cap)} across ${lines.length} lines.`,
      cited: [project, ...lines.slice(0, 3)],
    });
  }

  // 5) Substitute candidates — auto-discovered SKUs with "(substitute)" in their dataset note.
  const substitutes = getActiveProjectGraph().nodes.filter((n) =>
    n.kind === "SKU" && typeof n.attrs["source"] === "string" && (n.attrs["source"] as string).includes("substitute"),
  );
  for (const s of substitutes) {
    rows.push({
      id: `sub-${s.id}`,
      when: "moments ago",
      icon: <Replace className="w-3.5 h-3.5" />,
      tone: "info",
      title: `Substitute proposed · ${s.label}`,
      body: `Lead ${valueLabel(s.attrs["leadDays"])}d · style match ${(Number(s.attrs["styleMatch"] ?? 0) * 100).toFixed(0)}%.`,
      cited: [s],
      cta: { label: "Review swap", tone: "primary" },
    });
  }

  // 6) Last render check.
  const project2 = getActiveProjectGraph().nodes.find((n) => n.kind === "Project");
  if (project2) {
    rows.push({
      id: `render-${project2.id}`,
      when: "before last render",
      icon: <Camera className="w-3.5 h-3.5" />,
      tone: "ok",
      title: "Pre-render check passed",
      body: "Style mix within tolerance, clearances met, all approved SKUs lit.",
      cited: [project2],
    });
  }

  return rows.slice(0, 10);
}

interface ProjectActivityFeedProps {
  activeStageHint?: string | undefined;
}

export function ProjectActivityFeed({ activeStageHint }: ProjectActivityFeedProps): JSX.Element {
  const rows = useMemo(() => deriveActivity(), []);
  const project = getActiveProjectGraph().nodes.find((n) => n.kind === "Project");

  return (
    <div className="panel p-5 sm:p-7">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="eyebrow">What the agent did on this project</div>
          <div className="display-3 text-ink-950 mt-1 leading-tight">
            {project ? project.label : "Project activity"}
          </div>
          <div className="text-[13px] text-ink-700 mt-1.5">
            The last {rows.length} signals the agent caught — every line cites a real entity on this project.
          </div>
        </div>
        <span className="pill bg-fern/10 text-fern shrink-0">
          <Sparkles className="w-3 h-3" /> live
        </span>
      </div>

      <ul className="space-y-2.5">
        {rows.map((r, i) => (
          <motion.li
            key={r.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: i * 0.03 }}
            className={cls(
              "rounded-xl border px-3.5 py-3 flex gap-3 items-start",
              activeStageHint && r.id.startsWith(activeStageHint) ? "ring-1 ring-foyr/40" : "",
              "bg-cream",
            )}
          >
            <span className={cls("w-7 h-7 rounded-lg grid place-items-center shrink-0 border", TONE[r.tone])}>
              {r.icon}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-[13px] font-semibold text-ink-950 leading-tight truncate">{r.title}</div>
                <div className="text-[10.5px] uppercase tracking-wider text-ink-500 font-mono shrink-0">{r.when}</div>
              </div>
              <div className="text-[12px] text-ink-700 mt-0.5 leading-snug">{r.body}</div>
              {r.rulePredicate ? (
                <div className="text-[11px] text-ink-500 mt-1.5 italic leading-snug truncate">
                  {r.rulePredicate}
                </div>
              ) : null}
              {r.cited.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.cited.slice(0, 4).map((c) => (
                    <span key={c.id} className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">
                      <span className="font-mono text-[9px] text-ink-500">{c.kind}</span>
                      <span className="truncate max-w-[160px]">{c.label}</span>
                    </span>
                  ))}
                </div>
              ) : null}
              {r.cta ? (
                <button
                  className={cls(
                    "mt-2 btn !text-[11px] !py-1 !px-2",
                    r.cta.tone === "primary" ? "btn-foyr" : "btn-outline",
                  )}
                >
                  {r.cta.label}
                </button>
              ) : null}
            </div>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}
